import React from 'react';
import { useIntl } from 'react-intl';
import { ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { useGetSpotifyCanvasSettingsQuery } from '../../../../redux/api/fymAPI';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { TypedMemo } from '../../../common/types';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { spotifyCanvasMessages, spotifyCanvasSwitchMessages as m } from './spotifyCanvasMessages';
import { canvasDay } from './spotifyCanvasTexts';
import { ServiceExtrasSwitch } from '../extras/ServiceExtrasSwitch';
import { SpotifyCanvasTermsDialog } from './SpotifyCanvasTermsDialog';
import { useActiveArtistName } from './useActiveArtistName';
import { useSpotifyCanvasSwitch } from './useSpotifyCanvasSwitch';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Canvas row; each part uses its own slice of it */
import styles from './SpotifyCanvas.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SpotifyCanvasCardSwitchProps {
  connection: ServiceConnection;
}

/**
 * The auto upload switch on the Spotify for Artists card of Catalog → Service connections (design
 * spotify-canvas b5). The same setting as the Canvas row (one per artist), the same PUT and the same
 * terms dialog. A card that is not connected shows it too; a signed-out card keeps its place. The
 * caller shows it only with `canUseAutoUpload`.
 */
const SpotifyCanvasCardSwitchComponent: React.FC<SpotifyCanvasCardSwitchProps> = ({ connection }) => {
  const intl = useIntl();
  const accountId = useAccountId();
  const isDemo = useIsDemoAccount();
  const artistName = useActiveArtistName();
  const { data } = useGetSpotifyCanvasSettingsQuery({ accountId: accountId ?? 0 }, { skip: accountId === null });
  const settings = data?.settings;
  const toggle = useSpotifyCanvasSwitch({ accountId: accountId ?? 0, settings, readOnly: isDemo });
  const label = intl.formatMessage(m.label);
  const acceptedAt = settings?.termsAcceptedAt ?? null;

  let sub = intl.formatMessage(m.cardOff);
  if (isDemo) {
    sub = intl.formatMessage(m.demo);
  } else if (toggle.isOn && connection.status === ServiceConnectionStatus.expired) {
    sub = intl.formatMessage(m.cardSignedOut);
  } else if (toggle.isOn && acceptedAt !== null) {
    sub = intl.formatMessage(m.termsAccepted, { date: canvasDay(intl, acceptedAt) });
  } else if (toggle.isOn) {
    sub = intl.formatMessage(m.forArtist, { artist: artistName });
  }

  return (
    <div className={styles.cardBox} data-testid="spotify-canvas-card-switch">
      <div className={styles.switchLine}>
        <div>
          <b>{label}</b>
          <small>{withBrandWord(sub)}</small>
        </div>
        <ServiceExtrasSwitch
          label={label}
          isOn={!isDemo && toggle.isOn}
          isSaving={toggle.isSaving}
          isDimmed={isDemo || settings === undefined}
          onToggle={toggle.toggle}
          testId="spotify-canvas-switch"
        />
      </div>
      {toggle.failed && (
        <span className={styles.error} role="alert">
          {intl.formatMessage(spotifyCanvasMessages.didNotWork)}
        </span>
      )}
      <SpotifyCanvasTermsDialog
        isOpen={toggle.termsOpen}
        mode="autoUpload"
        artistName={artistName}
        isBusy={toggle.isSaving}
        failed={toggle.termsFailed}
        onAccept={toggle.acceptAndTurnOn}
        onCancel={toggle.closeTerms}
      />
    </div>
  );
};

export const SpotifyCanvasCardSwitch = TypedMemo(SpotifyCanvasCardSwitchComponent);
